// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — SaleTrackingStepper Component (FEAT-SALE-SMART-STATUS-DASHBOARD-001)
// ─────────────────────────────────────────────────────────────────────────────
// Interactive, visual lifecycle stepper tracking an order from Draft to
// Delivered, including active return lifecycle branches, timestamps, and
// responsible actors. Responsive: horizontal on desktop, vertical on mobile.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import {
  FileEdit,
  Bookmark,
  CheckCircle2,
  Truck,
  PackageCheck,
  RotateCcw,
  AlertCircle,
  Clock,
  User,
} from 'lucide-react';
import type { SaleTrackingEntry } from '@/hooks/useSales';

export interface SaleTrackingStepperProps {
  saleStatus: string;
  returnStatus?: string | null;
  trackingEntries?: SaleTrackingEntry[];
}

interface StepConfig {
  id: string;
  label: string;
  sublabel: string;
  icon: React.ElementType;
}

const FULFILLMENT_STEPS: StepConfig[] = [
  { id: 'draft',            label: 'Draft',             sublabel: 'Order created',         icon: FileEdit },
  { id: 'booked',           label: 'Booked',            sublabel: 'Stock reserved',        icon: Bookmark },
  { id: 'confirmed',        label: 'Confirmed',         sublabel: 'Ready for dispatch',    icon: CheckCircle2 },
  { id: 'delivery_pending', label: 'Delivery Pending',  sublabel: 'In transit to customer', icon: Truck },
  { id: 'delivered',        label: 'Delivered',         sublabel: 'Fulfillment completed',  icon: PackageCheck },
];

const STATUS_RANK: Record<string, number> = {
  draft: 1,
  booked: 2,
  confirmed: 3,
  delivery_pending: 4,
  delivered: 5,
  partially_returned: 5,
  returned: 5,
};

function formatStepDate(iso?: string) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export default function SaleTrackingStepper({
  saleStatus,
  returnStatus,
  trackingEntries = [],
}: SaleTrackingStepperProps) {
  const currentRank = STATUS_RANK[saleStatus] ?? 1;
  const isCancelled = saleStatus === 'cancelled';
  const hasReturn = !!returnStatus || ['partially_returned', 'returned'].includes(saleStatus);

  // Map latest timestamps and actors per status from audit history
  const stepMeta = useMemo(() => {
    const meta: Record<string, { timestamp: string; actor: string | null }> = {};
    for (const entry of trackingEntries) {
      const toStatus = (entry.metadata?.to as string) || (entry.new_data?.sale_status as string);
      if (toStatus && !meta[toStatus]) {
        meta[toStatus] = {
          timestamp: entry.created_at,
          actor: entry.actor_name,
        };
      }
    }
    return meta;
  }, [trackingEntries]);

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" /> Order Fulfillment Lifecycle
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Real-time status progression and milestone timeline
          </p>
        </div>
        {isCancelled && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1 text-xs font-semibold text-destructive">
            <AlertCircle className="h-3.5 w-3.5" /> Order Cancelled
          </span>
        )}
      </div>

      {/* Desktop Horizontal Stepper */}
      <div className="hidden md:block">
        <ol className="grid grid-cols-5 gap-2 relative">
          {FULFILLMENT_STEPS.map((step, idx) => {
            const stepRank = idx + 1;
            const isCompleted = !isCancelled && currentRank > stepRank;
            const isCurrent = !isCancelled && currentRank === stepRank;
            const StepIcon = step.icon;
            const meta = stepMeta[step.id];

            return (
              <li
                key={step.id}
                className="flex flex-col items-center text-center relative group"
                aria-current={isCurrent ? 'step' : undefined}
              >
                {/* Connector line */}
                {idx < FULFILLMENT_STEPS.length - 1 && (
                  <div
                    className={`absolute top-4 left-1/2 w-full h-0.5 -z-0 transition-colors ${
                      currentRank > stepRank && !isCancelled
                        ? 'bg-primary'
                        : 'bg-muted-foreground/25'
                    }`}
                    aria-hidden="true"
                  />
                )}

                {/* Step Icon Badge */}
                <div
                  className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full transition-all ${
                    isCurrent
                      ? 'bg-primary text-primary-foreground ring-4 ring-primary/20 shadow-sm'
                      : isCompleted
                      ? 'bg-primary text-primary-foreground'
                      : 'border-2 border-border bg-muted/40 text-muted-foreground'
                  }`}
                >
                  <StepIcon className="h-4 w-4" />
                </div>

                {/* Label & Description */}
                <div className="mt-2.5">
                  <p
                    className={`text-xs font-bold transition-colors ${
                      isCurrent
                        ? 'text-primary'
                        : isCompleted
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight max-w-[130px]">
                    {step.sublabel}
                  </p>
                  {meta && (
                    <div className="mt-1.5 flex flex-col items-center text-[10px] text-muted-foreground/90 font-mono">
                      <span>{formatStepDate(meta.timestamp)}</span>
                      {meta.actor && (
                        <span className="flex items-center gap-1 font-sans text-muted-foreground mt-0.5">
                          <User className="h-2.5 w-2.5" /> {meta.actor}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Mobile Vertical Stepper */}
      <div className="md:hidden space-y-3">
        <ol className="space-y-4">
          {FULFILLMENT_STEPS.map((step, idx) => {
            const stepRank = idx + 1;
            const isCompleted = !isCancelled && currentRank > stepRank;
            const isCurrent = !isCancelled && currentRank === stepRank;
            const StepIcon = step.icon;
            const meta = stepMeta[step.id];

            return (
              <li
                key={step.id}
                className="flex items-start gap-3 relative"
                aria-current={isCurrent ? 'step' : undefined}
              >
                {idx < FULFILLMENT_STEPS.length - 1 && (
                  <div
                    className={`absolute top-7 left-3.5 w-0.5 h-full -translate-x-1/2 -z-0 ${
                      currentRank > stepRank && !isCancelled
                        ? 'bg-primary'
                        : 'bg-muted-foreground/25'
                    }`}
                  />
                )}
                <div
                  className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                    isCurrent
                      ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                      : isCompleted
                      ? 'bg-primary text-primary-foreground'
                      : 'border-2 border-border bg-muted/40 text-muted-foreground'
                  }`}
                >
                  <StepIcon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0 pb-2">
                  <div className="flex items-center justify-between">
                    <p
                      className={`text-xs font-bold ${
                        isCurrent
                          ? 'text-primary'
                          : isCompleted
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {step.label}
                    </p>
                    {meta && (
                      <span className="text-[10px] font-mono text-muted-foreground">
                        {formatStepDate(meta.timestamp)}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">{step.sublabel}</p>
                  {meta?.actor && (
                    <p className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
                      <User className="h-2.5 w-2.5" /> {meta.actor}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Return Lifecycle Branch Banner (if applicable) */}
      {hasReturn && (
        <div className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3.5 flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <RotateCcw className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0 text-xs">
            <p className="font-semibold text-foreground flex items-center gap-2">
              Return Lifecycle Active
              <span className="rounded bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase">
                {returnStatus ? `Return ${returnStatus}` : saleStatus.replace(/_/g, ' ')}
              </span>
            </p>
            <p className="text-muted-foreground mt-0.5">
              {returnStatus === 'requested' && 'Return has been requested and is awaiting review.'}
              {returnStatus === 'accepted' && 'Return approved by manager. Awaiting physical item receipt and completion.'}
              {returnStatus === 'rejected' && 'Return request was reviewed and rejected.'}
              {returnStatus === 'completed' && 'Return has been received, restocked, and refunded.'}
              {!returnStatus && ['partially_returned', 'returned'].includes(saleStatus) &&
                'Items on this sale have been returned. Replacements can be marked Delivered.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
